import { CloseOutlined, SaveOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { callNotify } from "../../../..";
import Icon, { ICON_NAME } from "../../../../components/Icon";
import modal from "../../../../components/Popup/ModalCustom";
import { colors } from "../../../../contexts/WorldMapContext/Polyline";
import { RouteService } from "../../../../util/services/route";
import { useSelector } from "../../../../util/store/store";
import { diseasedZone } from "../../../../util/varibles/constants";
import { showErrorResponse } from "../../../../util/varibles/global";
import { IRoute } from "../../../../util/varibles/interface";
import { notify, NotifyCode } from "../../../../util/varibles/message";
import { RouteMode } from "../../constatns";
import { routeActions } from "../../reducer";
import styles from './style.module.scss';

const Diseases: React.FC<{ diseases: IRoute['diseases'] }> = ({ diseases = [] }) => {
    if (!(diseases || []).length)
        return null;

    return <div className="text-warn pl-[38px] leading-[18px] w-full">
        {diseases.map(item => diseasedZone[item]?.name).join(", ")}
    </div>
}

interface IProps {
    data: IRoute
    index: number
}

const RouteItem: React.FC<IProps> = (props) => {
    const { data, index } = props;
    const { id, name: rootName } = props.data;
    const [isFocus, setIsFocus] = useState(false);
    const [name, setName] = useState(rootName);
    const [loading, setLoading] = useState(false);
    const showId = useSelector(state => state.route.showId);
    const dispatch = useDispatch();

    const closeEdit = useCallback(() => {
        setIsFocus(false);
        setName(rootName);
    }, [rootName]);

    useEffect(() => {
        const handleClose = (e: MouseEvent) => {
            const { target } = e;
            if (target) {
                const el = (target as HTMLElement).closest(`.${styles['route-container']}`);
                if (el && el.id === 'route-row-' + id)
                    return false;
                closeEdit();
                document.removeEventListener('click', handleClose);
                e.preventDefault();
            }
        }

        document.addEventListener('click', handleClose);
        return () => {
            document.removeEventListener('click', handleClose);
        };
    }, [isFocus, closeEdit, id]);

    const handleSetDefault = () => {
        if (loading)
            return;
        setLoading(true);
        RouteService.setDefault(id)
            .then(() => {
                dispatch(routeActions.updateDefault(id))
                notify.success(NotifyCode.S2)([rootName]);
                setLoading(false);
            })
            .catch(error => {
                showErrorResponse('Update failed', error)
                setLoading(false);
            })
    }

    const handleDeleteRoute = () => {
        if (loading)
            return;
        modal.confirm({
            content: `Are you sure you want to delete this route "${rootName}"?`,
            ok: {
                click: () => {
                    setLoading(true);
                    RouteService.delete(id)
                        .then(({ default_id = '' }) => {
                            dispatch(routeActions.deleteRoute(id))
                            if (data.default) {
                                dispatch(routeActions.updateDefault(default_id))
                            }
                            notify.success(NotifyCode.S4)([rootName]);
                            setLoading(false);
                        })
                        .catch(error => {
                            showErrorResponse('Delete failed', error)
                            setLoading(false);
                        })
                }
            }
        })
    };

    const handleEditName = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        setIsFocus(true);
        e.preventDefault();
    }

    const handleChange = (e: any) => {
        const { value } = e.target;
        setName(value.trim());
        e.preventDefault();
        return false;
    }

    const handleSave = (e: any) => {
        if (name.length <= 0) {
            callNotify?.error({ title: 'Error', description: 'Name is required' });
            setTimeout(() => {
                const el = document.getElementById(id + '-input');
                if (el)
                    el.focus();
            }, 300)
            return false;
        }
        const { id: router_id, source_id, destination_id, default: isDefault, type, points } = data;
        const params = { router_id, source_id, destination_id, default: isDefault, type, name, points };
        setLoading(true);
        RouteService.update(params)
            .then(rs => {
                const data = { ...params, ...rs };
                dispatch(routeActions.updateRoute(data));
                notify.success(NotifyCode.S2)([rootName]);
                setLoading(false);
                setIsFocus(false);
            })
            .catch(error => {
                showErrorResponse('Update failed', error)
                setLoading(false);
            })
        e.preventDefault();
        return false;
    }

    return <div
        id={'route-row-' + id}
        className={styles['route-container']}
        onClick={() => dispatch(routeActions.show(index))}
        data-selected={index === showId}
        data-is-focus={isFocus}
    >
        <Spin spinning={loading}>
            <div className="flex flex-col">
                <div className="flex">
                    <div className={styles['route-info']}>
                        <div className={styles['route-color']} style={{ background: colors[index % 10] }} />
                        <div
                            className={styles['route-name']}
                            onDoubleClick={handleEditName}
                            title="Double click to edit route name"
                        >
                            {isFocus
                                ? <input
                                    id={id + '-input'}
                                    className={styles['route-input']}
                                    value={name}
                                    onChange={handleChange}
                                    autoFocus
                                />
                                : <span>{`${rootName} ${data.distance ? `(${data.distance} nm)` : ''}`}</span>}
                        </div>
                    </div>
                    <div className={styles.routeRightSide}>
                        {data.default ? <div className={styles['route-default']}>Default</div>
                            : <div className={styles['route-set-default']} onMouseDown={handleSetDefault}>
                                Set default
                            </div>}
                        <div className={styles.routeAction}>
                            <div className="bt-ghost" data-icon="true" title="Edit route" onClick={() => dispatch(routeActions.changeMode({ mode: RouteMode.EDIT, route: { ...data, index } }))}>
                                <Icon icon={ICON_NAME.EDIT} />
                            </div>
                            <div className="bt-ghost" data-icon="true" data-danger="true" title="Delete route" onClick={handleDeleteRoute}>
                                <Icon icon={ICON_NAME.DELETE} danger />
                            </div>
                        </div>
                        <div className={styles['action-edit-name']}>
                            <div className={styles['bt-cancel']} onMouseDown={closeEdit}><CloseOutlined /></div>
                            <div className={styles['bt-save']} onMouseDown={handleSave}><SaveOutlined /></div>
                        </div>
                    </div>
                </div>
                <Diseases diseases={data.diseases} />
            </div>
        </Spin>
    </div>
};

export default RouteItem;
